/*
 * The three states a list is in before it has rows: loading, empty, failed. Every list in the
 * product — a conversation, the directory, the files tab, Activity — used to draw its own, and a
 * reader met seven "Loading …" paragraphs and six empty screens with no way out. These are the
 * one shape for each.
 *
 * `Loading` is a skeleton in the shape of rows (a face and two lines), shown only after
 * `--delay-reveal` so a load that takes 100 ms never flashes one, with the same sentence said once
 * to a screen reader. `anchor: "end"` pushes the rows to the foot of the scroller, which is where a
 * conversation's rows land when they arrive. A caller that also wants the skeleton *held* once it
 * is up gates its own render with `useDelayedFlag` — the hold cannot live here, because the caller
 * unmounts this component the moment the rows are in.
 *
 * `Empty` is a title, a lead and **one door**: an empty screen without an action is a dead end, so
 * the door is required by design and optional only in the type for the one state that truly has
 * none (a search with no hits, where the door is the query field above). Its title is a paragraph
 * under the frame's `h1`, and a heading of its own — `heading={1}` — on the one screen that is
 * nothing but this state.
 *
 * `Error` is the danger alert with *Try again* in its action slot, so a failed load looks like a
 * failed form and not like an empty list.
 */

import type { ReactNode } from "react"
import { t } from "../core/i18n/index.ts"
import { cx } from "../lib/cx.ts"
import { useDelayedFlag } from "../lib/motion.ts"
import { Button } from "./Button.tsx"
import { Alert } from "./FormAlert.tsx"
import "./ui.css"

interface LoadingProps {
	/** How many row shapes to draw; the conversation computes its own from the scroller's height. */
	rows?: number
	/** Where the rows stand in the scroller while they wait. */
	anchor?: "start" | "end"
	/** The sentence a screen reader hears, already translated; the catalogue's "Loading …" by default. */
	label?: string
}

function Loading({ rows = 3, anchor = "start", label }: LoadingProps) {
	const shown = useDelayedFlag(true)
	if (!shown) return null
	return (
		<div
			className={cx("tb-state", "tb-state--loading", anchor === "end" && "tb-state--end")}
			role="status"
		>
			<span className="tb-visually-hidden">{label ?? t("app.loading")}</span>
			{Array.from({ length: rows }, (_, row) => (
				<div key={row} className="tb-state__row" aria-hidden="true">
					<span className="tb-state__face" />
					<span className="tb-state__lines">
						<span className="tb-state__line tb-state__line--head" />
						<span className={cx("tb-state__line", row % 2 === 1 && "tb-state__line--short")} />
					</span>
				</div>
			))}
		</div>
	)
}

interface EmptyProps {
	/** A 96 px picture above the words — the rocket on the one screen that earns it. */
	illustration?: ReactNode
	title: ReactNode
	lead?: ReactNode
	/**
	 * Draws the title as a real heading of this level, for a screen whose whole content *is* this
	 * state and which therefore has no frame heading above it (the 404). A paragraph by default,
	 * because everywhere else the frame's own `h1` already stands over the list. A `role="heading"`
	 * with an `aria-level` would satisfy assistive technology and still leave
	 * `document.querySelector("h1")` empty — which is what a walk script, a scenario's
	 * `page.locator("h1")` and the next axe rule read.
	 */
	heading?: 1 | 2
	/** The one door out: a `primary` or `secondary` button, or a link wearing one. */
	action?: ReactNode
}

function Empty({ illustration, title, lead, heading, action }: EmptyProps) {
	const Title = heading === undefined ? "p" : heading === 1 ? "h1" : "h2"
	return (
		<div className="tb-state tb-state--empty">
			{illustration === undefined ? null : (
				<div className="tb-state__illustration" aria-hidden="true">
					{illustration}
				</div>
			)}
			<Title className="tb-state__title">{title}</Title>
			{lead === undefined ? null : <p className="tb-state__lead">{lead}</p>}
			{action === undefined ? null : <div className="tb-state__action">{action}</div>}
		</div>
	)
}

interface ErrorProps {
	/** Already translated. */
	message: ReactNode
	retry: () => void
}

function Error({ message, retry }: ErrorProps) {
	return (
		<Alert
			kind="danger"
			className="tb-state--error"
			action={<Button variant="neutral" size="sm" label={t("action.retry")} onClick={retry} />}
		>
			{message}
		</Alert>
	)
}

export const ListState = { Loading, Empty, Error }
